import GoogleCalendarConnection from "../components/calendar/GoogleCalendarConnection";
import { API_BASE } from '../lib/apiOrigin';
import AdminLanguageSelector from "../components/admin/AdminLanguageSelector";
import { useAdminLanguage } from "../i18n/adminTranslations";
import { useTheme } from "../theme/ThemeContext";
import ThemeToggle from "../theme/ThemeToggle";
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Lock,
  RefreshCw,
  Mail,
  Phone,
  MapPin,
  Filter,
  Download,
  Calendar,
  Image as ImageIcon,
  Inbox,
  Upload,
  Trash2,
  Plus,
  Trophy,
  Award,
  Share2,
  Star,
  KeyRound,
  ShieldCheck,
  Eye,
  EyeOff,
  FileText,
  MessageCircleQuestion,
  Type,
} from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import QuoteBuilderDialog from "../components/QuoteBuilderDialog";
import AskQuestionsDialog from "../components/AskQuestionsDialog";
import PaymentsTab from "../components/admin/PaymentsTab";
import BusinessInterestsTab from "../components/admin/BusinessInterestsTab";
import ContactMessagesTab from "../components/admin/ContactMessagesTab";
import InstallersTab from "../components/admin/InstallersTab";
import WorkerMessagesInbox from "../components/admin/WorkerMessagesInbox";
import JobDispatchTab from "../components/admin/JobDispatchTab";
import AdminMetrics from "../components/admin/AdminMetrics";
import CatalogSnapshot from "../components/admin/CatalogSnapshot";
import CatalogPricesTab from "../components/admin/CatalogPricesTab";
import ManualPaymentsTab from "../components/admin/ManualPaymentsTab";
import RecognitionsTab from "../components/admin/RecognitionsTab";
import SocialLinksTab from "../components/admin/SocialLinksTab";
import GoogleReviewsTab from "../components/admin/GoogleReviewsTab";
import { tokenStorage } from "../lib/secureStorage";

const API = API_BASE;
const STATUS_OPTIONS = ["new", "contacted", "needs_info", "quoted", "changes_requested", "scheduled", "in-progress", "completed", "won", "lost"];
// Lazy-load batch size for the leads list. Renders this many at first and
// reveals more as the admin clicks "Show more" — keeps the DOM snappy at 40+.
const PAGE_SIZE = 20;
// localStorage key for the Dyslexia / Large-Text accessibility mode.
const LARGE_TEXT_KEY = "xa.admin.largeText";
// localStorage key for the light/midnight theme preference.

const TABS = [
  { id: "leads", label: "Leads", Icon: Inbox },
  { id: "dispatch", label: "Distribute jobs", Icon: Share2 },
  { id: "reports", label: "Reports", Icon: FileText },
  { id: "installers", label: "Installers", Icon: ShieldCheck },
  { id: "worker-messages", label: "Worker messages", Icon: Inbox },
  { id: "payments", label: "Payments & completion", Icon: FileText },
  { id: "manual-payments", label: "Venmo / Cash App", Icon: FileText },
  { id: "catalog-prices", label: "Quote catalog", Icon: FileText },
  { id: "contact-messages", label: "Contact inbox", Icon: Inbox },
  { id: "business-interests", label: "Pros & partners", Icon: Inbox },
  { id: "gallery", label: "Gallery", Icon: ImageIcon },
  { id: "awards", label: "Awards", Icon: Trophy },
  { id: "recognitions", label: "Recognitions", Icon: Award },
  { id: "social", label: "Social", Icon: Share2 },
  { id: "reviews", label: "Reviews", Icon: Star },
  { id: "settings", label: "Settings", Icon: KeyRound },
];

const Admin = () => {
  const { lang, a } = useAdminLanguage();
  const [token, setToken] = useState(() => tokenStorage.get());
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("leads");
  const [dispatchLeadId, setDispatchLeadId] = useState("");
  const [verifying, setVerifying] = useState(false);
  // Show/hide toggle for the login password input so the admin can confirm
  // what they typed (especially useful for tokens with mixed case / symbols).
  const [showLoginToken, setShowLoginToken] = useState(false);
  const authGeneration = useRef(0);
  const loginBusy = useRef(false);
  useEffect(() => () => { authGeneration.current += 1; }, []);
  const revokeSession = (sessionToken) => {
    if (sessionToken) axios.post(`${API}/admin/logout`, {}, { headers: { "X-Admin-Token": sessionToken } }).catch(() => {});
  };
  // SECURITY (Feb 2026): every page load with a cached token must first
  // re-validate it against the backend. Without this, an old/leaked token
  // sitting in sessionStorage would render the dashboard shell before the
  // first API call returns 401 — flashing leads briefly to an attacker.
  // `tokenChecked` stays false until /api/admin/verify succeeds.
  const [tokenChecked, setTokenChecked] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setTokenChecked(true);
      return;
    }
    setTokenChecked(false);
    axios
      .post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Token": token } })
      .then((res) => {
        if (cancelled) return;
        if (res.data?.session_epoch != null) tokenStorage.setEpoch(res.data.session_epoch);
        setTokenChecked(true);
      })
      .catch(() => {
        if (cancelled) return;
        // Token is stale / revoked / no longer valid — purge and show login.
        tokenStorage.clear();
        authGeneration.current += 1;
        loginBusy.current = false;
        setToken("");
        setInput("");
        setShowLoginToken(false);
        setTokenChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);
  // Accessibility: large-text / dyslexia-friendly mode. Persisted in
  // localStorage so the admin's choice survives reloads.
  const [largeText, setLargeText] = useState(() => {
    try {
      return localStorage.getItem(LARGE_TEXT_KEY) === "1";
    } catch (e) {
      return false;
    }
  });
  const toggleLargeText = () => {
    setLargeText((v) => {
      const next = !v;
      try {
        localStorage.setItem(LARGE_TEXT_KEY, next ? "1" : "0");
      } catch (e) {
        // Storage may be unavailable (private browsing) — toggle still works
        // in-memory for the current session.
      }
      return next;
    });
  };
  const { theme } = useTheme();
  // Global axios interceptor — automatically attach X-Session-Epoch to any
  // outbound request that carries X-Admin-Token, so revoke-all-sessions
  // instantly logs out every browser tab on its next request.
  useEffect(() => {
    const id = axios.interceptors.request.use((cfg) => {
      const hdrs = cfg.headers || {};
      const hasAdmin =
        hdrs["X-Admin-Token"] || hdrs["x-admin-token"] || hdrs["X-ADMIN-TOKEN"];
      if (hasAdmin) {
        const ep = tokenStorage.getEpoch();
        if (ep) hdrs["X-Session-Epoch"] = ep;
        cfg.headers = hdrs;
      }
      return cfg;
    });
    return () => axios.interceptors.request.eject(id);
  }, []);



  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    if (!input.trim()) {
      setError("Please enter the admin token.");
      return;
    }
    if (loginBusy.current) return;
    loginBusy.current = true;
    const attempt = ++authGeneration.current;
    setVerifying(true);
    try {
      // Verify against backend BEFORE saving the token. This way the user
      // gets immediate feedback if the password is wrong, instead of
      // appearing to log in and then watching every API call 401.
      const res = await axios.post(
        `${API}/admin/verify`,
        {},
        { headers: { "X-Admin-Token": input } }
      );
      if (attempt !== authGeneration.current) {
        revokeSession(res.data?.session_token);
        return;
      }
      // Save both the token and the current session_epoch so future requests
      // include X-Session-Epoch and are auto-invalidated after Revoke All.
      if (!res.data?.session_token) throw new Error("Login returned no session");
      setInput("");
      setShowLoginToken(false);
      setToken(res.data.session_token);
      tokenStorage.set(res.data.session_token);
      if (res.data?.session_epoch != null) tokenStorage.setEpoch(res.data.session_epoch);
      // Already verified — skip the re-verify-on-mount pass.
      setTokenChecked(true);
    } catch (err) {
      if (attempt !== authGeneration.current) return;
      if (err?.response?.status === 401) {
        setError("Invalid admin token. Try again.");
      } else if (
        err?.code === "ERR_NETWORK" ||
        err?.response?.status === 520 ||
        err?.response?.status === 502 ||
        err?.response?.status === 503
      ) {
        setError(
          "Backend is unreachable. Check production deployment status and try again."
        );
      } else {
        setError(
          err?.response?.data?.detail ||
            err?.message ||
            "Login failed. Please try again."
        );
      }
    } finally {
      if (attempt === authGeneration.current) { loginBusy.current = false; setVerifying(false); }
    }
  };

  // SECURITY: don't reveal the dashboard while a cached token is still
  // being validated against the backend. Brief, non-revealing spinner.
  if (token && !tokenChecked) {
    return (
      <div lang={lang} className="min-h-screen bg-[#09090b] flex items-center justify-center px-6" data-testid="admin-verifying-screen">
        <div className="text-zinc-400 text-sm uppercase tracking-[0.25em] animate-pulse">
          {a("Verifying session…")}
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div lang={lang} className="min-h-screen bg-[#09090b] flex items-center justify-center px-6">
        <form
          data-testid="admin-login-form"
          onSubmit={handleLogin}
          className="w-full max-w-sm bg-zinc-950 border border-white/10 p-8 space-y-5"
        >
          <div className="flex flex-wrap items-center gap-3">
            <ThemeToggle compact /><Lock size={20} className="text-sky-400" />
            <h1 className="font-display font-bold text-white text-xl">
              {a("Xavier Admin")}
            </h1>
          </div>
          <AdminLanguageSelector />
          <p className="text-zinc-400 text-sm">
            {a("Enter the admin token to view leads.")}
          </p>
          <div className="relative">
            <input
              data-testid="admin-token-input"
              type={showLoginToken ? "text" : "password"}
              placeholder={a("Admin token")}
              aria-label={a("Admin token")}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={verifying}
              autoComplete="current-password"
              className="w-full bg-transparent border-b border-white/15 focus:border-sky-400 text-white py-3 pr-10 outline-none disabled:opacity-50"
            />
            <button
              type="button"
              data-testid="admin-toggle-show-token"
              onClick={() => setShowLoginToken((s) => !s)}
              aria-label={a(showLoginToken ? "Hide token" : "Show token")}
              title={a(showLoginToken ? "Hide token" : "Show token")}
              className="absolute right-0 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-sky-400 p-2 transition-colors"
            >
              {showLoginToken ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={verifying}
            className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {a(verifying ? "Verifying…" : "Sign In")}
          </button>
          {error && (
            <p data-testid="admin-login-error" className="text-red-400 text-sm">
              {a(error)}
            </p>
          )}
        </form>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen ${theme === "light" ? "bg-[#f4f5f7]" : "bg-[#09090b]"} ${largeText ? "xa-large-text" : ""}`}
      lang={lang}
      data-large-text={largeText ? "on" : "off"}
      data-theme={theme}
    >
      <div className={`border-b sticky top-0 z-10 ${theme === "light" ? "bg-white border-zinc-200" : "border-white/5 bg-black"}`}>
        <div className="mx-auto max-w-7xl px-6 py-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <BrandLogo size="h-14 w-14" />
            <div>
              <div className={`font-display font-bold ${theme === "light" ? "text-zinc-900" : "text-white"}`}>
                {a("Admin Dashboard")}
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-sky-500">
                Xavier Assembly
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <AdminLanguageSelector />
            <ThemeToggle testId="admin-toggle-theme" />
            <button
              data-testid="admin-change-password-shortcut"
              onClick={() => setTab("settings")}
              title={a("Change admin password")}
              className={`inline-flex items-center gap-2 text-sm font-semibold border px-4 py-2 transition-colors ${
                theme === "light"
                  ? "text-zinc-700 border-zinc-300 hover:border-sky-500 hover:text-sky-600"
                  : "text-zinc-200 hover:text-white border-white/15 hover:border-sky-400"
              }`}
            >
              <KeyRound size={16} />
              <span className="hidden sm:inline">{a("Change password")}</span>
            </button>
            <button
              data-testid="admin-toggle-large-text"
              onClick={toggleLargeText}
              title={a("Toggle Large Text / Dyslexia-Friendly mode")}
              className={`inline-flex items-center gap-2 text-sm font-semibold border px-4 py-2 transition-colors ${
                largeText
                  ? "text-sky-500 border-sky-400 bg-sky-500/10"
                  : theme === "light"
                  ? "text-zinc-700 border-zinc-300 hover:border-sky-500 hover:text-sky-600"
                  : "text-zinc-200 hover:text-white border-white/15 hover:border-sky-400"
              }`}
            >
              <Type size={16} />
              <span className="hidden sm:inline">{a(largeText ? "Standard" : "Large text")}</span>
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={() => {
                const endingSession = token;
                authGeneration.current += 1;
                loginBusy.current = false;
                setToken("");
                setInput("");
                setShowLoginToken(false);
                tokenStorage.clear();
                revokeSession(endingSession);
              }}
              className={`text-sm font-semibold border px-4 py-2 transition-colors ${
                theme === "light"
                  ? "text-zinc-700 border-zinc-300 hover:border-sky-500 hover:text-sky-600"
                  : "text-zinc-200 hover:text-white border-white/15 hover:border-sky-400"
              }`}
            >
              {a("Sign out")}
            </button>
          </div>
        </div>
        <div className="mx-auto max-w-7xl px-6">
          <label className="block py-3 text-sm font-semibold">{a('Open section')}
            <select data-testid="admin-section-select" className="ml-3 max-w-full rounded border border-sky-400 bg-zinc-950 px-4 py-3 text-white" value={tab} onChange={event => setTab(event.target.value)}>
              {TABS.map(item => <option key={item.id} value={item.id}>{a(item.label)}</option>)}
            </select>
          </label>
          <div className="flex gap-0 overflow-x-auto">
            {TABS.map((tb) => (
              <button
                key={tb.id}
                data-testid={`admin-tab-${tb.id}`}
                onClick={() => setTab(tb.id)}
                className={`flex items-center gap-2.5 px-6 py-4 text-lg font-display font-bold uppercase tracking-[0.15em] border-b-2 transition-colors whitespace-nowrap ${
                  tab === tb.id
                    ? "text-sky-400 border-sky-400"
                    : "text-zinc-300 border-transparent hover:text-white hover:border-white/20"
                }`}
              >
                <tb.Icon size={18} />
                {a(tb.label)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-10">
        {tab === "settings" && <GoogleCalendarConnection apiBase={API} headers={{ "X-Admin-Token": token, "X-Session-Epoch": tokenStorage.getEpoch() || "" }} role="owner" language={lang} authReady={tokenChecked && !!token} />}
        {(() => {
          const handleAuthFail = () => {
            authGeneration.current += 1;
            loginBusy.current = false;
            setToken("");
            setInput("");
            setShowLoginToken(false);
            tokenStorage.clear();
          };
          const handlePasswordRotated = () => {
            // Force re-login with the new password
            authGeneration.current += 1;
            loginBusy.current = false;
            setToken("");
            setInput("");
            setShowLoginToken(false);
            tokenStorage.clear();
          };
          if (tab === "payments") return <PaymentsTab token={token} />;
          if (tab === "manual-payments") return <ManualPaymentsTab token={token} />;
          if (tab === "catalog-prices") return <CatalogPricesTab token={token} />;
          if (tab === "contact-messages") return <ContactMessagesTab token={token} onAuthFail={handleAuthFail} />;
          if (tab === "business-interests") return <BusinessInterestsTab token={token} onAuthFail={handleAuthFail} />;
          if (tab === "worker-messages") return <WorkerMessagesInbox token={token} lang={lang} />;
          if (tab === "installers") return <InstallersTab token={token} />;
          if (tab === "dispatch") return <JobDispatchTab token={token} initialLeadId={dispatchLeadId} />;
          if (tab === "reports") return <AdminMetrics token={token} />;
          if (tab === "leads") return <LeadsTab token={token} onAuthFail={handleAuthFail} onDispatch={id => { setDispatchLeadId(id); setTab("dispatch"); }} />;
          if (tab === "gallery") return <GalleryTab token={token} />;
          if (tab === "awards") return <AwardsTab token={token} onSwitchTab={setTab} />;
          if (tab === "recognitions") return <RecognitionsTab token={token} />;
          if (tab === "social") return <SocialLinksTab token={token} />;
          if (tab === "reviews") return <GoogleReviewsTab token={token} />;
          return <SettingsTab token={token} onRotated={handlePasswordRotated} />;
        })()}
      </div>
    </div>
  );
};

// ---------------- LEADS TAB ----------------
const LeadsTab = ({ token, onAuthFail, onDispatch }) => {
  const { a, statusLabel } = useAdminLanguage();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("");
  const [sizeFilter, setSizeFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  // Lazy-load count — start with PAGE_SIZE rows and add more on demand.
  // Resets to PAGE_SIZE whenever filter changes so the new view starts clean.
  const [visible, setVisible] = useState(PAGE_SIZE);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/leads/summary`, {
        headers: { "X-Admin-Token": token },
      });
      setQuotes(res.data);
    } catch (e) {
      // Show the error but DO NOT auto-logout on 401 — a transient
      // backend hiccup or rate-limit hit should not kick the admin out
      // of the dashboard. They can manually Sign out if needed.
      setError(
        e?.response?.status === 401
          ? "Invalid admin token — click Sign out (top right) and sign in again."
          : "Failed to load leads. Click refresh to retry."
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  // Refresh the leads list whenever a quote is sent from the QuoteBuilderDialog
  // so the auto-bumped lead status (-> "quoted") appears immediately.
  useEffect(() => {
    const handler = () => load();
    window.addEventListener("xa:lead-quoted", handler);
    return () => window.removeEventListener("xa:lead-quoted", handler);
  }, [load]);

  const [exporting, setExporting] = useState(false);
  const exportCsv = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      // Blob download: browsers can't attach custom headers to a plain <a href>,
      // so we fetch the CSV via axios and trigger a synthetic click.
      const res = await axios.get(`${API}/admin/leads/export.csv`, {
        headers: { "X-Admin-Token": token },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const stamp = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 12);
      const a = document.createElement("a");
      a.href = url;
      a.download = `xavier-leads-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(
        e?.response?.status === 401
          ? "Session expired — Sign out and sign in again to export."
          : "Failed to export leads. Try again in a moment."
      );
    } finally {
      setExporting(false);
    }
  }, [exporting, token]);

  const updateStatus = async (id, status, opts = {}) => {
    // `silent: true` means the backend already moved the lead (e.g. via the
    // Ask-Questions endpoint) — we just need to mirror it locally without
    // re-issuing the PATCH call.
    if (opts.silent) {
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      return;
    }
    try {
      await axios.patch(
        `${API}/quotes/${id}?status=${status}`,
        {},
        { headers: { "X-Admin-Token": token } }
      );
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
    } catch (e) {
      setError("Failed to update status");
    }
  };

  const filtered = quotes.filter((q) => {
    if (filter !== "all" && q.status !== filter) return false;
    if (cityFilter && !(q.city || "").toLowerCase().includes(cityFilter.toLowerCase())) return false;
    if (sizeFilter && !(q.product_size || "").toLowerCase().includes(sizeFilter.toLowerCase())) return false;
    if (searchQuery) {
      const s = searchQuery.toLowerCase();
      const hay = [q.name, q.email, q.phone, q.city, q.state, q.product_size, q.service]
        .map((x) => (x || "").toLowerCase())
        .join(" ");
      if (!hay.includes(s)) return false;
    }
    return true;
  });
  // Distinct cities/sizes for the dropdown pickers, sorted alphabetically.
  const uniqueCities = Array.from(new Set(quotes.map((q) => (q.city || "").trim()).filter(Boolean))).sort();
  const uniqueSizes = Array.from(new Set(quotes.map((q) => (q.product_size || "").trim()).filter(Boolean))).sort();
  const stats = STATUS_OPTIONS.reduce(
    (acc, s) => ({ ...acc, [s]: quotes.filter((q) => q.status === s).length }),
    {}
  );
  // Lazy-loading: derive how many rows to render without a useEffect.
  // We track only the user's "Show more" expansions and clip on render so
  // switching filters never inherits a bigger window than needed.
  const visibleRows = filtered.slice(0, visible);
  const hasMore = filtered.length > visibleRows.length;
  const setFilterAndReset = (next) => {
    setFilter(next);
    setVisible(PAGE_SIZE);
  };
  const clearFilters = () => {
    setFilter("all");
    setCityFilter("");
    setSizeFilter("");
    setSearchQuery("");
    setVisible(PAGE_SIZE);
  };
  const activeFilterCount = [
    filter !== "all",
    !!cityFilter,
    !!sizeFilter,
    !!searchQuery,
  ].filter(Boolean).length;

  return (
    <>
      <div className="flex items-center justify-between mb-10 gap-3 flex-wrap">
        <h2 className="font-display font-black text-white text-4xl sm:text-5xl">{a("Leads")}</h2>
        <div className="flex items-center gap-2">
          <button
            data-testid="admin-export-csv-button"
            onClick={exportCsv}
            disabled={exporting || quotes.length === 0}
            title={a(quotes.length === 0 ? "No leads to export yet" : "Download all leads as CSV")}
            className="h-12 px-4 border border-white/15 text-white hover:border-sky-400 flex items-center gap-2 text-sm uppercase tracking-[0.15em] font-bold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download size={18} className={exporting ? "animate-pulse" : ""} />
            {a(exporting ? "Exporting…" : "Export CSV")}
          </button>
          <button
            data-testid="admin-refresh-button"
            aria-label={a("Refresh")}
            onClick={load}
            className="h-12 w-12 border border-white/15 text-white hover:border-sky-400 flex items-center justify-center"
          >
            <RefreshCw size={20} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-5 mb-12">
        <StatCard
          label={a("Total")}
          value={quotes.length}
          active={filter === "all"}
          onClick={() => setFilterAndReset("all")}
          testId="stat-total"
        />
        {STATUS_OPTIONS.map((s) => (
          <StatCard
            key={s}
            label={statusLabel(s)}
            value={stats[s]}
            active={filter === s}
            onClick={() => setFilterAndReset(s)}
            testId={`stat-${s}`}
          />
        ))}
      </div>

      <div className="flex flex-col gap-4 mb-7">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-300 mb-1.5 block">{a("Search")}</span>
            <input
              data-testid="leads-search"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setVisible(PAGE_SIZE); }}
              placeholder={a("Name, email, phone, service…")}
              className="w-full bg-zinc-900 border border-white/15 focus:border-sky-400 text-white px-3.5 py-2.5 text-base outline-none"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-300 mb-1.5 block">{a("City")}</span>
            <select
              data-testid="leads-filter-city"
              value={cityFilter}
              onChange={(e) => { setCityFilter(e.target.value); setVisible(PAGE_SIZE); }}
              className="w-full bg-zinc-900 border border-white/15 focus:border-sky-400 text-white px-3.5 py-2.5 text-base outline-none"
            >
              <option value="">{a("All cities")}</option>
              {uniqueCities.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-300 mb-1.5 block">{a("Product size")}</span>
            <select
              data-testid="leads-filter-size"
              value={sizeFilter}
              onChange={(e) => { setSizeFilter(e.target.value); setVisible(PAGE_SIZE); }}
              className="w-full bg-zinc-900 border border-white/15 focus:border-sky-400 text-white px-3.5 py-2.5 text-base outline-none"
            >
              <option value="">{a("All sizes")}</option>
              {uniqueSizes.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="flex items-center justify-between gap-3 text-base text-zinc-200">
          <div className="flex items-center gap-3 flex-wrap">
            <Filter size={18} className="text-sky-400" />
            <span className="uppercase tracking-[0.2em] font-semibold text-sm">
              {a("Status:")} <span className="text-sky-400">{statusLabel(filter)}</span>
            </span>
            {activeFilterCount > 0 && (
              <button
                data-testid="leads-clear-filters"
                onClick={clearFilters}
                className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-300 hover:text-sky-300 border border-white/15 hover:border-sky-400 px-3 py-1.5"
              >
                {a("Clear filters")} ({activeFilterCount})
              </button>
            )}
          </div>
          <div className="text-zinc-400 text-sm" data-testid="leads-count">
            {a("Showing {visible} of {total} leads", { visible: visibleRows.length, total: filtered.length })}
          </div>
        </div>
      </div>

      {error && <p className="text-red-300 text-base mb-5 bg-red-500/10 border border-red-500/30 px-4 py-3">{a(error)}</p>}

      <div data-testid="quotes-list" className="space-y-5">
        {filtered.length === 0 ? (
          <div className="border border-white/10 p-12 text-center text-zinc-300 text-base">
            {a("No leads yet.")}
          </div>
        ) : (
          visibleRows.map((q) => (
            <LeadRow key={q.id} q={q} token={token} onStatusChange={updateStatus} onDispatch={onDispatch} />
          ))
        )}
      </div>

      {hasMore && (
        <div className="mt-8 flex justify-center">
          <button
            data-testid="leads-show-more"
            onClick={() => setVisible((v) => v + PAGE_SIZE)}
            className="inline-flex items-center gap-2 border border-white/20 hover:border-sky-400 hover:text-sky-300 text-zinc-100 text-base uppercase tracking-[0.12em] font-bold px-6 py-3.5 min-h-[52px] transition-colors"
          >
            {a("Show {count} more", { count: Math.min(PAGE_SIZE, filtered.length - visible) })}
          </button>
        </div>
      )}
    </>
  );
};

const StatCard = ({ label, value, active, onClick, testId }) => {
  // "changes_requested" is our longest label — tightening tracking + letting
  // long words wrap prevents overflow on the narrow stat cards in the grid.
  const isLong = label && label.length > 10;
  const prettyLabel = label ? label.replace(/_/g, " ") : label;
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`text-left border p-6 transition-all ${
        active
          ? "border-sky-400 bg-sky-500/10"
          : "border-white/10 hover:border-white/30 bg-zinc-950/40"
      }`}
    >
      <div
        className={`eyebrow !text-zinc-300 !text-sm mb-3 font-semibold break-words leading-snug ${
          isLong ? "tracking-[0.08em]" : "tracking-[0.18em]"
        }`}
      >
        {prettyLabel}
      </div>
      <div className="font-display font-black text-white text-4xl sm:text-5xl tracking-tight">{value}</div>
    </button>
  );
};

const LeadTimeline = ({ leadId, token, refreshKey = 0 }) => {
  const { a } = useAdminLanguage();
  const [events, setEvents] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/leads/${leadId}/timeline`, {
          headers: { "X-Admin-Token": token },
        });
        if (!cancelled) setEvents(res.data?.events || []);
      } catch (e) {
        if (!cancelled) setError("Failed to load timeline.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, token, refreshKey]);

  const kindStyles = {
    lead_created:      { dot: "bg-sky-400",     label: "Created" },
    status_change:     { dot: "bg-emerald-400", label: "Status" },
    quote_sent:        { dot: "bg-amber-400",   label: "Quote sent" },
    email_event:       { dot: "bg-indigo-400",  label: "Email" },
    customer_response: { dot: "bg-fuchsia-400", label: "Customer" },
    admin_question:    { dot: "bg-teal-400",    label: "Question" },
  };

  return (
    <div data-testid={`lead-timeline-${leadId}`}>
      <div className="eyebrow !text-sm !text-zinc-300 mb-4 font-semibold tracking-[0.18em]">{a("Timeline")}</div>
      {error && (
        <div className="text-red-300 text-base bg-red-500/10 border border-red-500/30 px-4 py-3">
          {a(error)}
        </div>
      )}
      {!events && !error && (
        <div className="text-zinc-400 text-base">{a("Loading…")}</div>
      )}
      {events && events.length === 0 && (
        <div className="text-zinc-400 text-base">{a("No events recorded yet.")}</div>
      )}
      {events && events.length > 0 && (
        <ol className="relative pl-6 border-l border-white/15 space-y-5">
          {events.map((ev, i) => {
            const style = kindStyles[ev.kind] || { dot: "bg-zinc-400", label: ev.kind };
            const at = ev.at ? new Date(ev.at) : null;
            return (
              <li key={i} className="relative" data-testid={`timeline-event-${i}`}>
                <span
                  className={`absolute -left-[30px] top-1.5 w-3.5 h-3.5 rounded-full ${style.dot} ring-2 ring-zinc-950`}
                />
                <div className="flex flex-wrap items-baseline gap-x-3">
                  <span className="text-xs uppercase tracking-[0.18em] font-bold text-zinc-400">
                    {a(style.label)}
                  </span>
                  <span className="text-white text-base sm:text-lg font-semibold">
                    {ev.label}
                  </span>
                </div>
                {ev.detail && (
                  <div className="text-zinc-300 text-base mt-1 leading-relaxed">{ev.detail}</div>
                )}
                <div className="text-zinc-500 text-xs mt-1.5">
                  {at ? at.toLocaleString() : ""}
                  {ev.source ? ` · source: ${ev.source}` : ""}
                  {ev.actor ? ` · ${ev.actor}` : ""}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

const LeadRow = ({ q: summary, token, onStatusChange, onDispatch }) => {
  const { a, statusLabel } = useAdminLanguage();
  const [details, setDetails] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(false);
  const detailEpoch = useRef(0);
  const q = { ...details, ...summary };
  useEffect(() => { detailEpoch.current += 1; setDetails(null); return () => { detailEpoch.current += 1; }; }, [token]);
  const loadDetails = async () => {
    if (!summary.photos_deferred || details) return true;
    const epoch = detailEpoch.current; setDetailLoading(true); setDetailError(false);
    try {
      const result = await axios.get(`${API}/quotes/${encodeURIComponent(summary.id)}`, { headers: { 'X-Admin-Token': token }, timeout: 20000 });
      if (epoch !== detailEpoch.current) return false;
      if (result.data?.id !== summary.id) throw new Error('Invalid lead details');
      setDetails(result.data); return true;
    } catch { if (epoch === detailEpoch.current) setDetailError(true); return false; }
    finally { if (epoch === detailEpoch.current) setDetailLoading(false); }
  };
  const [open, setOpen] = useState(false);
  const [quoteBuilderOpen, setQuoteBuilderOpen] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  // Bump this counter when an action lands so the Timeline component
  // remounts and reloads its events list.
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0);
  const date = new Date(q.created_at);
  const hasExtras =
    (q.photos && q.photos.length) ||
    (q.backyard_photos && q.backyard_photos.length) ||
    (q.inspiration_photos && q.inspiration_photos.length) ||
    (q.product_links && q.product_links.length) ||
    (q.addons && q.addons.length) ||
    (q.accessories && q.accessories.length) ||
    (q.cleanup_services && q.cleanup_services.length) ||
    (q.optional_addons && q.optional_addons.length) ||
    q.product_type || q.product_size || q.install_location ||
    q.ground_type || q.delivery_date || q.complexity ||
    q.structure_material || q.roof_type || q.shed_material ||
    q.foundation_type || q.platform_option || q.backyard_access ||
    q.electrical_access || q.special_instructions ||
    q.already_delivered || q.boxes_count || q.hoa_required ||
    q.hoa_restrictions || q.has_manual ||
    q.structure_brand || q.purchased_from || q.delivery_status ||
    q.estimated_delivery_date || q.needs_platform_built ||
    q.existing_surface || q.needs_teardown || q.needs_relocation ||
    // New required Y/N gates + scheduling + acknowledgments
    q.has_blinds !== null && q.has_blinds !== undefined ||
    q.needs_anchoring !== null && q.needs_anchoring !== undefined ||
    q.has_led !== null && q.has_led !== undefined ||
    q.needs_pallet_removal !== null && q.needs_pallet_removal !== undefined ||
    q.surface_type || q.preferred_date || q.time_window ||
    q.product_delivered !== null && q.product_delivered !== undefined ||
    q.ack_15ft || q.ack_clear_access || q.ack_site_prep ||
    q.ack_access_conditions || q.ack_price_protection;

  return (
    <div
      data-testid={`lead-row-${q.id}`}
      className="border border-white/10 bg-zinc-950 hover:border-sky-400/50 transition-colors"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-7">
        <div className="lg:col-span-3">
          <div className="font-display font-black text-white text-3xl sm:text-4xl leading-tight tracking-tight" data-testid={`lead-name-${q.id}`}>
            {q.name}
          </div>
          <div className="text-zinc-300 text-sm uppercase tracking-[0.18em] mt-3 font-semibold">
            {date.toLocaleString()}
          </div>
          <div className="mt-3 inline-flex items-center flex-wrap gap-2 text-xs uppercase tracking-[0.18em] text-sky-300 font-bold">
            <span className="bg-sky-500/15 border border-sky-400/40 px-2.5 py-1.5">
              {q.language?.toUpperCase()}
            </span>
            {q.photos && q.photos.length > 0 && (
              <span className="bg-sky-500/10 border border-sky-400/40 px-2.5 py-1.5">
                📷 {q.photos.length}
              </span>
            )}
            {q.backyard_photos && q.backyard_photos.length > 0 && (
              <span className="bg-sky-500/10 border border-sky-400/40 px-2.5 py-1.5">
                🌳 {q.backyard_photos.length}
              </span>
            )}
            {q.inspiration_photos && q.inspiration_photos.length > 0 && (
              <span className="bg-sky-500/10 border border-sky-400/40 px-2.5 py-1.5">
                💡 {q.inspiration_photos.length}
              </span>
            )}
            {q.product_links && q.product_links.length > 0 && (
              <span className="bg-sky-500/10 border border-sky-400/40 px-2.5 py-1.5">
                🔗 {q.product_links.length}
              </span>
            )}
            {q.addons && q.addons.length > 0 && (
              <span className="bg-sky-500/10 border border-sky-400/40 px-2.5 py-1.5">
                + {q.addons.length} addon
              </span>
            )}
          </div>
        </div>
        <div className="lg:col-span-3 space-y-3 text-lg">
          <a href={`mailto:${q.email}`} data-testid={`lead-email-${q.id}`} className="flex items-center gap-3 text-white hover:text-sky-400 break-all font-medium">
            <Mail size={18} className="text-sky-400 shrink-0" />
            {q.email}
          </a>
          <a href={`tel:${q.phone}`} data-testid={`lead-phone-${q.id}`} className="flex items-center gap-3 text-white hover:text-sky-400 font-medium">
            <Phone size={18} className="text-sky-400 shrink-0" />
            {q.phone}
          </a>
          {q.service_address && <p className="text-sm text-zinc-300">{q.service_address}</p>}
          {(q.location || q.city || q.state) && (
            <div className="flex items-center gap-3 text-zinc-200 font-medium">
              <MapPin size={18} className="text-sky-400 shrink-0" />
              {q.location || [q.city, q.state].filter(Boolean).join(", ")}
            </div>
          )}
        </div>
        <div className="lg:col-span-3 space-y-3">
          <button type="button" data-testid={`lead-dispatch-${q.id}`} onClick={() => onDispatch?.(q.id)} className="w-full min-h-[52px] rounded border border-sky-400 px-4 py-3 font-bold text-sky-300 hover:bg-sky-500/15">{a('Distribute job')}</button>
          <div className="text-white text-xl sm:text-2xl font-display font-bold leading-tight" data-testid={`lead-service-${q.id}`}>{q.service}</div>
          {(q.product_type || q.product_size) && (
            <div className="text-zinc-200 text-base">
              {q.product_type} {q.product_size && `· ${q.product_size}`}
            </div>
          )}
          {q.scheduled_date && (
            <div
              data-testid={`lead-scheduled-${q.id}`}
              className="inline-flex items-center gap-2 border border-emerald-400/40 bg-emerald-500/10 text-emerald-300 text-xs uppercase tracking-[0.15em] font-bold px-3 py-1.5"
            >
              {"→"} {a("Scheduled:")} {q.scheduled_date}
              {q.scheduled_window && ` · ${q.scheduled_window}`}
            </div>
          )}
          {q.message && (
            <p className="text-zinc-200 text-base leading-relaxed line-clamp-2">
              {q.message}
            </p>
          )}
          <div className="flex items-center gap-5 text-sm text-zinc-300 pt-1">
            <span className="font-semibold">{a("Admin email submission:")} {q.notification_status?.admin === "provider_accepted" ? <span className="text-emerald-400">{a("Provider accepted")}</span> : <span className="text-zinc-400">{a(q.email_sent ? "Recorded (legacy)" : "Not confirmed")}</span>}</span>
            <button
              onClick={async () => { if (open) setOpen(false); else if (await loadDetails()) setOpen(true); }}
              disabled={detailLoading}
              data-testid={`lead-expand-${q.id}`}
              className="text-sky-400 hover:text-sky-300 hover:underline font-semibold text-base"
            >
              {a(open ? "Hide details" : "View details")}
            </button>
          </div>
        </div>
        <div className="lg:col-span-3 space-y-3">
          {detailError && <p role="alert">{a('Unable to load job details. Try again.')}</p>}
          {q.quote_mode && <div data-testid={`lead-quote-mode-${q.id}`} className={`text-sm font-bold uppercase tracking-[0.1em] border px-3 py-2 ${q.quote_mode === "auto" ? "border-sky-400/50 text-sky-300" : "border-amber-400/50 text-amber-300"}`}>
            {q.quote_mode === "auto" ? `${a("Automatic quote")} · $${Number(q.installation_price || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })} · ${a(q.auto_quote?.sent ? "Sent" : "Not sent")}` : a("Manual review")}
            {q.quote_mode !== "auto" && (q.auto_quote?.reasons || []).length > 0 && <span className="block normal-case tracking-normal font-normal text-xs text-zinc-400 mt-1">{q.auto_quote.reasons.join(", ")}</span>}
          </div>}
          <select
            data-testid={`lead-status-${q.id}`}
            aria-label={`${a("Status")}: ${q.name}`}
            value={q.status}
            onChange={(e) => onStatusChange(q.id, e.target.value)}
            className="w-full bg-zinc-900 border border-white/15 text-white text-base font-semibold uppercase tracking-[0.12em] py-3.5 px-4 outline-none focus:border-sky-400 hover:border-white/30 transition-colors min-h-[52px]"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {statusLabel(s)}
              </option>
            ))}
          </select>
          <button
            data-testid={`lead-ask-questions-${q.id}`}
            onClick={() => setAskOpen(true)}
            className="w-full inline-flex items-center justify-center gap-2.5 border border-white/20 hover:border-sky-400 hover:text-sky-300 bg-zinc-950 text-zinc-100 text-sm sm:text-base uppercase tracking-[0.1em] font-bold py-4 px-5 min-h-[52px] break-words transition-colors"
          >
            <MessageCircleQuestion size={16} className="shrink-0" />
            <span>{a("Ask Questions")}</span>
          </button>
          <button
            data-testid={`lead-build-quote-${q.id}`}
            onClick={async () => { if (await loadDetails()) setQuoteBuilderOpen(true); }}
            disabled={detailLoading}
            className="w-full inline-flex items-center justify-center gap-2.5 bg-sky-500 hover:bg-sky-400 text-white text-sm sm:text-base uppercase tracking-[0.1em] font-bold py-4 px-5 min-h-[52px] break-words transition-colors shadow-[0_4px_16px_rgba(14,165,233,0.35)]"
          >
            <FileText size={16} className="shrink-0" />
            <span>{a("Create / Send Quote")}</span>
          </button>
        </div>
      </div>

      <QuoteBuilderDialog
        open={quoteBuilderOpen}
        onOpenChange={setQuoteBuilderOpen}
        lead={q}
        token={token}
      />
      <AskQuestionsDialog
        open={askOpen}
        onOpenChange={setAskOpen}
        lead={q}
        token={token}
        onSent={(data) => {
          // Refresh status pill + timeline immediately
          if (data?.status) onStatusChange(q.id, data.status, { silent: true });
          setTimelineRefreshKey((k) => k + 1);
        }}
      />

      {open && (
        <div className="border-t border-white/10 p-6 sm:p-7 bg-zinc-950/50 space-y-7" data-testid={`lead-details-${q.id}`}>
          <LeadTimeline leadId={q.id} token={token} refreshKey={timelineRefreshKey} />
          <CatalogSnapshot selection={q.catalog_selection} result={q.catalog_result} />
          {(q.product_type || q.product_size || q.structure_brand || q.purchased_from || q.delivery_status || q.estimated_delivery_date) && (
            <div>
              <div className="eyebrow !text-xs !text-zinc-300 mb-3 font-semibold">{a("Product & Purchase")}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-4 text-sm">
                {q.product_type && <Detail label={a("Product")} value={q.product_type} />}
                {q.structure_brand && <Detail label={a("Brand")} value={q.structure_brand} />}
                {q.purchased_from && <Detail label={a("Purchased from")} value={q.purchased_from} />}
                {q.product_size && <Detail label={a("Size")} value={q.product_size} />}
                {q.delivery_status && <Detail label={a("Delivery")} value={q.delivery_status} />}
                {q.estimated_delivery_date && <Detail label={a("Est. delivery")} value={q.estimated_delivery_date} />}
              </div>
            </div>
          )}

          {(q.structure_material || q.roof_type || q.shed_material || q.foundation_type || q.platform_option || q.existing_surface) && (
            <div>
              <div className="eyebrow !text-xs !text-zinc-300 mb-3 font-semibold">{a("Configuration")}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-4 text-sm">
                {q.structure_material && <Detail label={a("Material")} value={q.structure_material} />}
                {q.roof_type && <Detail label={a("Roof")} value={q.roof_type} />}
                {q.shed_material && <Detail label={a("Shed type")} value={q.shed_material} />}
                {q.foundation_type && <Detail label={a("Foundation")} value={q.foundation_type} />}
                {q.platform_option && <Detail label={a("Platform")} value={q.platform_option} />}
                {q.existing_surface && <Detail label={a("Existing surface")} value={q.existing_surface} />}
              </div>
            </div>
          )}

          {(q.install_location || q.ground_type || q.delivery_date || q.complexity || q.backyard_access) && (
            <div>
              <div className="eyebrow !text-xs !text-zinc-300 mb-3 font-semibold">{a("Install Site")}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-4 text-sm">
                {q.install_location && <Detail label={a("Install")} value={q.install_location} />}
                {q.ground_type && <Detail label={a("Ground")} value={q.ground_type} />}
                {q.backyard_access && <Detail label={a("Access")} value={q.backyard_access} />}
                {q.delivery_date && <Detail label={a("Target date")} value={q.delivery_date} />}
                {q.complexity && <Detail label={a("Complexity")} value={q.complexity} />}
              </div>
            </div>
          )}

          {/* Required Y/N answers + scheduling + agreement */}
          {(q.has_blinds !== null && q.has_blinds !== undefined ||
            q.needs_anchoring !== null && q.needs_anchoring !== undefined ||
            q.has_led !== null && q.has_led !== undefined ||
            q.needs_pallet_removal !== null && q.needs_pallet_removal !== undefined ||
            q.product_delivered !== null && q.product_delivered !== undefined ||
            q.preferred_date || q.time_window || q.surface_type) && (
            <div>
              <div className="eyebrow !text-xs !text-zinc-300 mb-3 font-semibold">{a("Booking Answers")}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-4 text-sm">
                {q.surface_type && <Detail label={a("Surface")} value={q.surface_type} />}
                {q.preferred_date && <Detail label={a("Preferred date")} value={q.preferred_date} />}
                {q.preferred_dates?.length > 1 && <Detail label={a("Preferred dates (not confirmed)")} value={q.preferred_dates.join(" · ")} />}
                {q.time_window && <Detail label={a("Time window")} value={q.time_window} />}
                {q.product_delivered !== null && q.product_delivered !== undefined && (
                  <Detail label={a("Product delivered")} value={q.product_delivered ? "Yes" : "No"} />
                )}
                {q.has_blinds !== null && q.has_blinds !== undefined && (
                  <Detail
                    label={a("Blinds")}
                    value={q.has_blinds ? `Yes (${q.blinds_qty || "?"})` : "No"}
                  />
                )}
                {q.needs_anchoring !== null && q.needs_anchoring !== undefined && (
                  <Detail
                    label={a("Anchoring")}
                    value={
                      q.needs_anchoring
                        ? `Yes — ${q.anchoring_count || "?"} anchors`
                        : "No"
                    }
                  />
                )}
                {q.has_led !== null && q.has_led !== undefined && (
                  <Detail label={a("LED lights")} value={q.has_led ? "Yes" : "No"} />
                )}
                {q.needs_pallet_removal !== null && q.needs_pallet_removal !== undefined && (
                  <Detail
                    label={a("Pallet removal")}
                    value={
                      q.needs_pallet_removal
                        ? `Yes (${q.pallets_qty || "?"} · $${(q.pallets_qty || 0) * 45})`
                        : "No"
                    }
                  />
                )}
              </div>
            </div>
          )}

          {/* Acknowledgments */}
          {(q.ack_15ft || q.ack_clear_access || q.ack_site_prep ||
            q.ack_access_conditions || q.ack_price_protection) && (
            <div>
              <div className="eyebrow !text-xs !text-zinc-300 mb-3 font-semibold">{a("Customer Acknowledgments")}</div>
              <div className="flex flex-wrap gap-2.5">
                {q.ack_15ft && <Pill>✓ 15ft rule</Pill>}
                {q.ack_clear_access && <Pill>✓ Clear access</Pill>}
                {q.ack_site_prep && <Pill>✓ Site prep $80</Pill>}
                {q.ack_access_conditions && <Pill>✓ Access conditions</Pill>}
                {q.ack_price_protection && <Pill>✓ Price protection</Pill>}
              </div>
            </div>
          )}

          {(q.needs_platform_built || q.needs_teardown || q.needs_relocation || q.electrical_access) && (
            <div>
              <div className="eyebrow !text-xs !text-zinc-300 mb-3 font-semibold">{a("Site Work Needed")}</div>
              <div className="flex flex-wrap gap-2.5">
                {q.needs_platform_built && <Pill>🪵 Build platform/foundation</Pill>}
                {q.needs_teardown && <Pill>🔨 Teardown old structure</Pill>}
                {q.needs_relocation && <Pill>📦 Relocate existing</Pill>}
                {q.electrical_access && <Pill>⚡ Power on site</Pill>}
              </div>
            </div>
          )}

          {(q.already_delivered || q.boxes_count || q.hoa_required || q.hoa_restrictions || q.has_manual) && (
            <div>
              <div className="eyebrow !text-xs !text-zinc-300 mb-3 font-semibold">{a("Qualification")}</div>
              <div className="flex flex-wrap gap-2.5">
                {q.already_delivered && <Pill>✓ Delivered</Pill>}
                {q.boxes_count && <Pill>📦 {q.boxes_count}</Pill>}
                {q.hoa_restrictions === "yes" && <Pill>⚠ HOA required</Pill>}
                {q.hoa_restrictions === "no" && <Pill>✓ No HOA</Pill>}
                {q.hoa_restrictions === "unknown" && <Pill>? HOA unknown</Pill>}
                {q.has_manual && <Pill>📘 Has manual</Pill>}
              </div>
            </div>
          )}

          {((q.accessories && q.accessories.length > 0) || (q.addons && q.addons.length > 0) || (q.cleanup_services && q.cleanup_services.length > 0) || (q.optional_addons && q.optional_addons.length > 0)) && (
            <div>
              <div className="eyebrow !text-xs !text-zinc-300 mb-3 font-semibold">{a("Add-ons / accessories")}</div>
              <div className="flex flex-wrap gap-2.5">
                {q.accessories && q.accessories.map((a) => <Pill key={a}>✨ {a.replace(/-/g, " ")}</Pill>)}
                {q.addons && q.addons.map((a) => <Pill key={a}>⚓ {a.replace(/-/g, " ")}</Pill>)}
                {q.cleanup_services && q.cleanup_services.map((a) => <Pill key={a}>🧹 {a.replace(/-/g, " ")}</Pill>)}
                {q.optional_addons && q.optional_addons.map((a) => <Pill key={a}>➕ {a.replace(/-/g, " ")}</Pill>)}
              </div>
            </div>
          )}

          {q.special_instructions && (
            <div>
              <div className="eyebrow !text-xs !text-zinc-300 mb-3 font-semibold">{a("Special instructions")}</div>
              <p className="text-zinc-100 text-base leading-relaxed border-l-2 border-sky-400 pl-4 py-1">
                {q.special_instructions}
              </p>
            </div>
          )}

          {q.product_links && q.product_links.length > 0 && (
            <div>
              <div className="eyebrow !text-xs !text-zinc-300 mb-3 font-semibold">{a("Product Links")}</div>
              <ul className="space-y-2 text-sm">
                {q.product_links.map((l) => (
                  <li key={l}>
                    <a href={l} target="_blank" rel="noreferrer" className="text-sky-400 hover:text-sky-300 hover:underline break-all font-medium">
                      {l}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {q.photos && q.photos.length > 0 && (
            <PhotoGrid id={q.id} title={a("Product Photos")} photos={q.photos} prefix="lead-photo" />
          )}
          {q.backyard_photos && q.backyard_photos.length > 0 && (
            <PhotoGrid id={q.id} title={a("Backyard Photos")} photos={q.backyard_photos} prefix="lead-backyard-photo" />
          )}
          {q.inspiration_photos && q.inspiration_photos.length > 0 && (
            <PhotoGrid id={q.id} title={a("Inspiration Photos")} photos={q.inspiration_photos} prefix="lead-inspiration-photo" />
          )}
        </div>
      )}
    </div>
  );
};

const PhotoGrid = ({ id, title, photos, prefix }) => (
  <div>
    <div className="eyebrow !text-xs !text-zinc-300 mb-3 font-semibold">{title} ({photos.length})</div>
    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
      {photos.map((p, i) => (
        <a
          key={i}
          href={p}
          target="_blank"
          rel="noreferrer"
          data-testid={`${prefix}-${id}-${i}`}
          className="aspect-square block border border-white/10 hover:border-sky-400 overflow-hidden"
        >
          <img src={p} alt={`${title} ${i + 1}`} className="w-full h-full object-cover" />
        </a>
      ))}
    </div>
  </div>
);

const Detail = ({ label, value }) => (
  <div>
    <div className="text-zinc-300 uppercase tracking-wider text-[11px] font-semibold mb-1">
      {label}
    </div>
    <div className="text-white text-sm font-medium">{value}</div>
  </div>
);

const Pill = ({ children }) => (
  <span className="inline-flex items-center bg-sky-500/15 border border-sky-400/40 text-sky-200 px-3 py-1.5 text-sm font-semibold">
    {children}
  </span>
);

// ---------------- GALLERY TAB ----------------
const TAG_OPTIONS = [
  "Gazebo",
  "Pergola",
  "Shed",
  "Greenhouse",
  "Playground",
  "Sauna",
  "Outdoor Furniture",
  "Fitness Equipment",
];

const GalleryTab = ({ token }) => {
  const { a } = useAdminLanguage();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    title: "",
    location: "",
    tag: "Gazebo",
    caption: "",
    order: 0,
    image_url: "",
  });
  const [preview, setPreview] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [newCat, setNewCat] = useState("");
  const [catSaving, setCatSaving] = useState(false);
  const fileRef = useRef(null);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/gallery`);
      setItems(res.data);
    } catch (e) {
      setError("Failed to load gallery");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/gallery-categories`);
      const cats = Array.isArray(res.data?.categories) ? res.data.categories : [];
      setCategories(cats);
      setForm((f) => (cats.includes(f.tag) ? f : { ...f, tag: cats[0] || "Other" }));
    } catch (e) {
      // fall back silently — public endpoint is unauth so a failure is unusual
    }
  }, []);

  useEffect(() => {
    loadItems();
    loadCategories();
  }, [loadItems, loadCategories]);

  const saveCategories = async (next) => {
    setCatSaving(true);
    try {
      const res = await axios.put(
        `${API}/admin/gallery-categories`,
        { categories: next },
        { headers: { "X-Admin-Token": token } }
      );
      setCategories(res.data?.categories || next);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to save categories");
    } finally {
      setCatSaving(false);
    }
  };

  const addCategory = async () => {
    const val = (newCat || "").trim();
    if (!val) return;
    if (categories.some((c) => c.toLowerCase() === val.toLowerCase())) {
      setNewCat("");
      return;
    }
    await saveCategories([...categories, val]);
    setNewCat("");
  };

  const removeCategory = async (cat) => {
    if (!window.confirm(`Remove category "${cat}"? Existing photos keep their tag but the filter chip disappears from the public site.`)) return;
    await saveCategories(categories.filter((c) => c !== cat));
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      setError("Image too large (max 6MB). Compress and try again.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      setForm((f) => ({ ...f, image_url: result }));
      setPreview(result);
      setError("");
    };
    reader.readAsDataURL(file);
  };

  const resetForm = () => {
    setForm({ title: "", location: "", tag: categories[0] || "Gazebo", caption: "", order: 0, image_url: "" });
    setPreview("");
    setEditingId(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || (!form.image_url && !editingId)) {
      setError("Title and image are required");
      return;
    }
    setError("");
    try {
      if (editingId) {
        // Don't send image_url unless the admin uploaded a new file — avoids
        // shipping the whole base64 payload back through the wire for a
        // caption-only edit.
        const payload = { title: form.title, location: form.location, tag: form.tag, caption: form.caption, order: form.order };
        if (form.image_url && form.image_url.startsWith("data:")) payload.image_url = form.image_url;
        const res = await axios.put(`${API}/gallery/${editingId}`, payload, {
          headers: { "X-Admin-Token": token },
        });
        setItems((it) => it.map((x) => (x.id === editingId ? res.data : x)));
      } else {
        const res = await axios.post(`${API}/gallery`, form, {
          headers: { "X-Admin-Token": token },
        });
        setItems((it) => [res.data, ...it]);
      }
      resetForm();
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    }
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setForm({
      title: it.title || "",
      location: it.location || "",
      tag: it.tag || categories[0] || "Other",
      caption: it.caption || "",
      order: it.order || 0,
      image_url: "",  // sentinel: only overwrite when a new file is picked
    });
    setPreview(it.image_url);
    if (fileRef.current) fileRef.current.value = "";
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm(a("Delete this photo?"))) return;
    try {
      await axios.delete(`${API}/gallery/${id}`, {
        headers: { "X-Admin-Token": token },
      });
      setItems((it) => it.filter((i) => i.id !== id));
      if (editingId === id) resetForm();
    } catch (e) {
      setError("Failed to delete");
    }
  };

  return (
    <>
      <h2 className="font-display font-black text-white text-3xl sm:text-4xl mb-3">
        {a("Gallery")}
      </h2>
      <p className="text-zinc-200 text-base mb-8 max-w-2xl leading-relaxed">
        Upload real project photos with captions. Categories let visitors filter
        the homepage gallery. JPG/PNG/WEBP up to 6MB.
      </p>

      {/* Category manager — sits above the upload form so admins customize
          the picklist first, then reuse it below. */}
      <div className="border border-white/10 bg-zinc-950 p-5 sm:p-6 mb-8">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="eyebrow !text-zinc-200 !text-sm font-semibold">{a("Categories")}</div>
          <div className="text-zinc-400 text-xs">Add, remove, or reorder — updates the public filter chips.</div>
        </div>
        <div className="flex flex-wrap gap-2 mb-4" data-testid="gallery-category-list">
          {categories.map((c) => (
            <span
              key={c}
              data-testid={`gallery-category-chip-${c}`}
              className="inline-flex items-center gap-2 border border-white/15 text-white text-xs uppercase tracking-[0.15em] font-bold px-3 py-1.5"
            >
              {c}
              <button
                type="button"
                onClick={() => removeCategory(c)}
                className="text-zinc-400 hover:text-red-400 transition-colors"
                data-testid={`gallery-category-remove-${c}`}
                aria-label={`Remove category ${c}`}
              >
                <Trash2 size={12} />
              </button>
            </span>
          ))}
          {categories.length === 0 && (
            <span className="text-zinc-400 text-sm">{a("Loading categories…")}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <input
            data-testid="gallery-category-new"
            value={newCat}
            onChange={(e) => setNewCat(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCategory())}
            placeholder={a("New category (e.g. Outdoor Kitchen)")}
            className="flex-1 bg-transparent border-b border-white/20 focus:border-sky-400 text-white text-base py-2.5 outline-none placeholder:text-zinc-500"
          />
          <button
            type="button"
            onClick={addCategory}
            disabled={catSaving || !newCat.trim()}
            data-testid="gallery-category-add"
            className="border border-white/15 hover:border-sky-400 text-white text-xs uppercase tracking-[0.15em] font-bold px-4 py-2.5 disabled:opacity-40"
          >
            <Plus size={14} className="inline mr-1" />
            {a("Add")}
          </button>
        </div>
      </div>

      <form
        data-testid="gallery-upload-form"
        onSubmit={submit}
        className="border border-white/10 bg-zinc-950 p-6 sm:p-8 mb-10 grid grid-cols-1 md:grid-cols-3 gap-7"
      >
        <div className="md:col-span-1">
          <label className="eyebrow !text-xs !text-zinc-200 mb-3 block font-semibold">
            {a("Photo")} {editingId && <span className="text-zinc-400 normal-case tracking-normal ml-1">(re-upload to replace, or leave to keep current)</span>}
          </label>
          <div
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-white/15 hover:border-sky-400 transition-colors aspect-video flex items-center justify-center cursor-pointer overflow-hidden"
            data-testid="gallery-upload-dropzone"
          >
            {preview ? (
              <img
                src={preview}
                alt="preview"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="text-center text-zinc-200 text-sm px-4">
                <Upload size={26} className="mx-auto mb-2 text-sky-400" />
                {a("Click to upload image")}
              </div>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={handleFile}
            className="hidden"
            data-testid="gallery-file-input"
          />
        </div>

        <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className="eyebrow !text-xs !text-zinc-200 mb-2.5 block font-semibold">{a("Title")}</label>
            <input
              data-testid="gallery-input-title"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Yardistry Meridian Gazebo"
              className="w-full bg-transparent border-b border-white/20 focus:border-sky-400 text-white text-base py-3 outline-none placeholder:text-zinc-500"
            />
          </div>
          <div>
            <label className="eyebrow !text-xs !text-zinc-200 mb-2.5 block font-semibold">{a("Location")}</label>
            <input
              data-testid="gallery-input-location"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="San Jose, CA"
              className="w-full bg-transparent border-b border-white/20 focus:border-sky-400 text-white text-base py-3 outline-none placeholder:text-zinc-500"
            />
          </div>
          <div>
            <label className="eyebrow !text-xs !text-zinc-200 mb-2.5 block font-semibold">{a("Category")}</label>
            <select
              data-testid="gallery-input-tag"
              value={form.tag}
              onChange={(e) => setForm({ ...form, tag: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 focus:border-sky-400 text-white text-base py-3 outline-none appearance-none"
            >
              {categories.map((t) => (
                <option key={t} value={t} className="bg-zinc-900">
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="eyebrow !text-xs !text-zinc-200 mb-2.5 block font-semibold">{a("Order")}</label>
            <input
              data-testid="gallery-input-order"
              type="number"
              value={form.order}
              onChange={(e) =>
                setForm({ ...form, order: parseInt(e.target.value) || 0 })
              }
              className="w-full bg-transparent border-b border-white/20 focus:border-sky-400 text-white text-base py-3 outline-none"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="eyebrow !text-xs !text-zinc-200 mb-2.5 block font-semibold">{a("Caption")}</label>
            <textarea
              data-testid="gallery-input-caption"
              value={form.caption}
              onChange={(e) => setForm({ ...form, caption: e.target.value })}
              rows={2}
              maxLength={400}
              placeholder="Two-day install with anchoring and LED add-ons. Level pad prepped by our team."
              className="w-full bg-transparent border-b border-white/20 focus:border-sky-400 text-white text-base py-3 outline-none placeholder:text-zinc-500 resize-none"
            />
            <div className="text-right text-[10px] text-zinc-500 mt-1">{form.caption.length}/400</div>
          </div>
          {error && (
            <div data-testid="gallery-error" className="sm:col-span-2 text-red-300 text-base bg-red-500/10 border border-red-500/30 px-4 py-3">
              {a(error)}
            </div>
          )}
          <div className="sm:col-span-2 flex gap-3">
            <button
              type="submit"
              data-testid="gallery-submit-button"
              className="btn-primary text-base px-7 py-3.5"
            >
              <Plus size={18} />
              {a(editingId ? "Save changes" : "Add to Gallery")}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                data-testid="gallery-cancel-edit"
                className="border border-white/15 hover:border-sky-400 text-white text-sm uppercase tracking-[0.15em] font-bold px-5"
              >
                {a("Cancel")}
              </button>
            )}
          </div>
        </div>
      </form>

      <div className="flex items-center justify-between mb-5">
        <div className="eyebrow !text-zinc-200 !text-sm font-semibold">
          {items.length} photo{items.length === 1 ? "" : "s"}
        </div>
        <button
          onClick={loadItems}
          className="h-11 w-11 border border-white/15 text-white hover:border-sky-400 flex items-center justify-center"
          data-testid="gallery-refresh-button"
          aria-label={a("Refresh")}
        >
          <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div data-testid="gallery-list" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.length === 0 ? (
          <div className="col-span-full border border-white/10 p-12 text-center text-zinc-300 text-base">
            {a("No photos yet. Upload your first project.")}
          </div>
        ) : (
          items.map((it) => (
            <div
              key={it.id}
              data-testid={`gallery-card-${it.id}`}
              className={`border ${editingId === it.id ? "border-sky-400" : "border-white/10"} bg-zinc-950 group relative`}
            >
              <img
                src={it.image_url}
                alt={it.title}
                className="w-full aspect-video object-cover"
              />
              <div className="absolute top-3 right-3 flex gap-2">
                <button
                  data-testid={`gallery-edit-${it.id}`}
                  onClick={() => startEdit(it)}
                  className="xa-media-dark h-10 w-10 bg-black/70 hover:bg-sky-500 text-white flex items-center justify-center transition-colors"
                  aria-label={a("Edit photo")}
                >
                  <FileText size={16} />
                </button>
                <button
                  data-testid={`gallery-delete-${it.id}`}
                  onClick={() => remove(it.id)}
                  className="xa-media-dark h-10 w-10 bg-black/70 hover:bg-red-500 text-white flex items-center justify-center transition-colors"
                  aria-label={a("Delete photo")}
                >
                  <Trash2 size={16} />
                </button>
              </div>
              <div className="p-5 space-y-1.5">
                <div className="font-display font-bold text-white text-lg leading-tight">
                  {it.title}
                </div>
                <div className="text-zinc-300 text-sm">
                  {it.location} · <span className="text-sky-300 font-semibold">{it.tag}</span> · order {it.order}
                </div>
                {it.caption && (
                  <div className="text-zinc-400 text-xs pt-1.5 leading-relaxed line-clamp-3">{it.caption}</div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
};

// ---------------- AWARDS TAB ----------------
const AWARD_SLOTS = [
  {
    key: "awards_main",
    label: "Featured 3-Year Award (wide / 16:9)",
    hint: "Best size: 1600×900px. Used as the hero featured trophy.",
  },
  {
    key: "awards_2023",
    label: "Nextdoor Neighborhood Fave — 2023",
    hint: "Square or portrait works best (1:1).",
  },
  {
    key: "awards_2024",
    label: "Nextdoor Neighborhood Fave — 2024",
    hint: "Square or portrait works best (1:1).",
  },
  {
    key: "awards_2025",
    label: "Nextdoor Neighborhood Fave — 2025",
    hint: "Square or portrait works best (1:1).",
  },
];

const AwardsTab = ({ token, onSwitchTab }) => {
  const { a } = useAdminLanguage();
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/settings/images`);
      setImages(res.data || {});
    } catch (e) {
      setError("Failed to load award images");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async (key, file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      setError(`${key}: image too large (max 6MB).`);
      return;
    }
    setError("");
    setSavingKey(key);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await axios.put(
          `${API}/settings/images/${key}`,
          { image_url: reader.result },
          { headers: { "X-Admin-Token": token } }
        );
        setImages((prev) => ({ ...prev, [key]: reader.result }));
      } catch (e) {
        setError(e?.response?.data?.detail || "Upload failed");
      } finally {
        setSavingKey(null);
      }
    };
    reader.readAsDataURL(file);
  };

  const remove = async (key) => {
    if (!window.confirm("Remove this award image?")) return;
    try {
      await axios.delete(`${API}/settings/images/${key}`, {
        headers: { "X-Admin-Token": token },
      });
      setImages((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (e) {
      setError("Failed to remove image");
    }
  };

  return (
    <>
      <h2 className="font-display font-black text-white text-3xl sm:text-4xl mb-3">
        {a("Recognition & Awards")}
      </h2>
      <p className="text-zinc-200 text-base mb-6 max-w-2xl leading-relaxed">
        Upload your custom 3-year award image and each official Nextdoor
        Neighborhood Fave badge (2023, 2024, 2025). They'll appear instantly on
        the homepage Awards section, replacing the gold placeholders.
      </p>

      {/* Pointer to the dynamic CRUD — the Awards tab is intentionally slot-based
          for the Nextdoor 3-year feature; anything else (new prize, certification,
          license, insurance) lives in Recognitions. */}
      <div
        data-testid="awards-recognitions-cta"
        className="mb-8 border border-sky-400/40 bg-sky-500/10 p-4 sm:p-5 flex items-center justify-between gap-4 flex-wrap"
      >
        <div className="min-w-0 flex-1">
          <div className="text-white font-semibold text-base mb-1">Adding a NEW award or certification?</div>
          <div className="text-zinc-300 text-sm leading-relaxed">
            This tab holds the four fixed Nextdoor slots. To add unlimited custom
            entries (with category, year, description) use the <strong className="text-sky-300">{a("Recognitions")}</strong> tab.
          </div>
        </div>
        <button
          type="button"
          onClick={() => onSwitchTab && onSwitchTab("recognitions")}
          data-testid="awards-goto-recognitions"
          className="border border-sky-400 text-sky-300 hover:bg-sky-500/20 text-xs uppercase tracking-[0.15em] font-bold px-4 py-2.5 whitespace-nowrap flex items-center gap-2"
        >
          <Plus size={14} /> {a("Add new")}
        </button>
      </div>

      {error && (
        <div data-testid="awards-error" className="text-red-300 text-base mb-5 bg-red-500/10 border border-red-500/30 px-4 py-3">
          {a(error)}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {AWARD_SLOTS.map((slot) => (
          <AwardSlot
            key={slot.key}
            slot={slot}
            image={images[slot.key]}
            saving={savingKey === slot.key}
            onUpload={(file) => upload(slot.key, file)}
            onRemove={() => remove(slot.key)}
          />
        ))}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <p className="text-zinc-200 text-sm font-semibold">
          {Object.keys(images).length} / {AWARD_SLOTS.length} slots filled
        </p>
        <button
          onClick={load}
          className="h-11 w-11 border border-white/15 text-white hover:border-sky-400 flex items-center justify-center"
          data-testid="awards-refresh-button"
        >
          <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
        </button>
      </div>
    </>
  );
};

const AwardSlot = ({ slot, image, saving, onUpload, onRemove }) => {
  const { a } = useAdminLanguage();
  const inputRef = useRef(null);
  return (
    <div
      data-testid={`award-slot-${slot.key}`}
      className="border border-white/10 bg-zinc-950 p-6"
    >
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <div className="font-display font-bold text-white text-lg leading-tight">
            {slot.label}
          </div>
          <div className="text-zinc-200 text-sm mt-1.5">{slot.hint}</div>
          <div className="mt-2 text-xs uppercase tracking-[0.18em] text-[#d4af37] font-bold">
            key: {slot.key}
          </div>
        </div>
        {image && (
          <button
            data-testid={`award-remove-${slot.key}`}
            onClick={onRemove}
            className="h-10 w-10 border border-white/15 text-zinc-300 hover:bg-red-500 hover:text-white hover:border-red-500 flex items-center justify-center transition-colors"
            aria-label={a("Remove image")}
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      <div
        onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed border-white/15 hover:border-[#d4af37] transition-colors aspect-video flex items-center justify-center cursor-pointer overflow-hidden bg-zinc-950/30"
        data-testid={`award-dropzone-${slot.key}`}
      >
        {image ? (
          <img
            src={image}
            alt={slot.label}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="text-center text-zinc-200 text-sm px-4">
            {saving ? (
              <RefreshCw size={24} className="mx-auto animate-spin text-[#d4af37]" />
            ) : (
              <>
                <Upload size={24} className="mx-auto mb-2 text-[#d4af37]" />
                {a("Click to upload")}
                <div className="text-xs mt-1.5 opacity-80">
                  JPG / PNG / WEBP · ≤ 6MB
                </div>
              </>
            )}
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={(e) => onUpload(e.target.files?.[0])}
        className="hidden"
        data-testid={`award-file-${slot.key}`}
      />
    </div>
  );
};

// ---------------- SETTINGS TAB ----------------
const SettingsTab = ({ token, onRotated }) => {
  const { a } = useAdminLanguage();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const strength = (() => {
    if (!next) return null;
    const len = next.length;
    const variety =
      (/[a-z]/.test(next) ? 1 : 0) +
      (/[A-Z]/.test(next) ? 1 : 0) +
      (/[0-9]/.test(next) ? 1 : 0) +
      (/[^a-zA-Z0-9]/.test(next) ? 1 : 0);
    if (len < 8) return { label: "Too short", className: "text-red-400 border-red-400/40" };
    if (variety <= 2) return { label: "Weak", className: "text-amber-400 border-amber-400/40" };
    if (variety === 3) return { label: "Good", className: "text-sky-400 border-sky-400/40" };
    return { label: "Strong", className: "text-emerald-400 border-emerald-400/40" };
  })();

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (next.length < 8) {
      setMsg({ type: "error", text: "New password must be at least 8 characters." });
      return;
    }
    if (next !== confirm) {
      setMsg({ type: "error", text: "New password and confirmation do not match." });
      return;
    }
    if (next === current) {
      setMsg({ type: "error", text: "New password must differ from current password." });
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/change-password`,
        { current_password: current, new_password: next },
        { headers: { "X-Admin-Token": token } }
      );
      setMsg({
        type: "success",
        text: "Password updated. You will be signed out in 3 seconds — sign in with the new password.",
      });
      setTimeout(() => onRotated(), 3000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const text =
        typeof detail === "string"
          ? detail
          : e?.response?.status === 429
          ? "Too many attempts. Wait a few minutes and try again."
          : "Failed to update password.";
      setMsg({ type: "error", text });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl">
      <div className="flex items-center gap-3 mb-6">
        <ShieldCheck size={26} className="text-sky-400" />
        <h2 className="font-display font-black text-3xl sm:text-4xl text-white">{a("Security")}</h2>
      </div>
      <p className="text-zinc-200 text-base mb-8 leading-relaxed">
        {a("Rotate the admin password used by the dashboard and any integrated clients (mobile app, internal tools). After rotation you'll be signed out automatically — sign in again with the new password.")}
      </p>

      <form
        onSubmit={submit}
        data-testid="settings-change-password-form"
        className="space-y-5 border border-white/10 bg-zinc-950/60 p-6"
      >
        <PasswordField
          label={a("Current password")}
          value={current}
          onChange={setCurrent}
          show={show}
          setShow={setShow}
          testId="settings-current-password"
        />
        <PasswordField
          label={a("New password (min 8 characters)")}
          value={next}
          onChange={setNext}
          show={show}
          setShow={setShow}
          testId="settings-new-password"
        />
        {strength && (
          <div className={`text-xs uppercase tracking-[0.18em] inline-flex items-center gap-2 border px-3 py-1.5 font-bold ${strength.className}`}>
            <span className="h-2 w-2 bg-current rounded-full" />
            {a(strength.label)}
          </div>
        )}
        <PasswordField
          label={a("Confirm new password")}
          value={confirm}
          onChange={setConfirm}
          show={show}
          setShow={setShow}
          testId="settings-confirm-password"
        />

        {msg && (
          <div
            data-testid="settings-password-msg"
            className={`text-base px-4 py-3 border-l-2 font-medium ${
              msg.type === "success"
                ? "border-emerald-400 text-emerald-100 bg-emerald-500/10"
                : "border-red-400 text-red-100 bg-red-500/10"
            }`}
          >
            {a(msg.text)}
          </div>
        )}

        <button
          type="submit"
          data-testid="settings-submit-password"
          disabled={busy || !current || !next || !confirm}
          className="btn-primary text-base px-7 py-3.5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {a(busy ? "Updating…" : "Update password")}
        </button>
      </form>

      <div className="mt-8 border border-white/10 bg-zinc-950/60 p-6">
        <div className="eyebrow !text-xs !text-zinc-300 mb-3 font-semibold">{a("How it works")}</div>
        <ul className="text-zinc-200 text-sm leading-relaxed space-y-2 list-disc pl-5">
          <li>The new password takes effect <strong className="text-white">immediately</strong> across the website admin and any clients using the <code className="text-sky-400">X-Admin-Token</code> header.</li>
          <li>You'll be signed out automatically and need to sign back in with the new password.</li>
          <li>Brute-force protected: 5 wrong attempts per 5 minutes per IP.</li>
          <li>Passwords are stored as <strong className="text-white">bcrypt</strong> hashes — never plaintext.</li>
        </ul>
      </div>

      <RevokeAllSessionsCard token={token} />
      <CalendarFeedCard token={token} />
    </div>
  );
};


// ---------------- Calendar Feed (Google/TimeTree) ----------------
const CalendarFeedCard = ({ token }) => {
  const { a } = useAdminLanguage();
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [rotating, setRotating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/calendar-feed`, {
        headers: { "X-Admin-Token": token },
      });
      setFeed(res.data);
    } catch (e) {
      // silent — user sees the "Enable feed" button below
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const rotate = async () => {
    if (!window.confirm(a("Regenerate the calendar feed URL? Existing subscriptions will stop updating until you add the new URL. Do this only if the URL leaked."))) return;
    setRotating(true);
    try {
      await axios.post(`${API}/admin/calendar-feed/rotate`, {}, {
        headers: { "X-Admin-Token": token },
      });
      await load();
    } finally {
      setRotating(false);
    }
  };

  const copy = async () => {
    if (!feed?.url) return;
    try {
      await navigator.clipboard.writeText(feed.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // fallback: select in a hidden input
      const el = document.createElement("input");
      el.value = feed.url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="mt-8 border border-white/10 bg-zinc-950/60 p-6" data-testid="calendar-feed-card">
      <div className="flex items-center gap-3 mb-3">
        <Calendar size={22} className="text-sky-400" />
        <h3 className="font-display font-bold text-xl text-white">{a("Private calendar feed (ICS)")}</h3>
      </div>
      <p className="text-zinc-300 text-sm leading-relaxed mb-5">
        {a("Subscribe to this private calendar feed in a compatible calendar app. The URL contains an access token: keep it private. Refresh timing depends on the app and device.")}
        {" "}{a("This is an ICS feed, not a TimeTree API integration. TimeTree discontinued its API on December 22, 2023.")}
      </p>

      {feed ? (
        <>
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-300 mb-2 block">
            {a("Your private feed URL")}
          </label>
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <input
              data-testid="calendar-feed-url"
              readOnly
              value={feed.url}
              onFocus={(e) => e.target.select()}
              className="flex-1 bg-zinc-950/40 border border-white/15 text-white text-xs sm:text-sm font-mono px-3 py-2.5 outline-none focus:border-sky-400"
            />
            <button
              type="button"
              onClick={copy}
              data-testid="calendar-feed-copy"
              className="border border-white/15 hover:border-sky-400 text-white text-xs uppercase tracking-[0.15em] font-bold px-4 py-2.5 whitespace-nowrap"
            >
              {a(copied ? "Copied ✓" : "Copy link")}
            </button>
          </div>

          <details className="mb-4 border border-white/10 p-4 bg-zinc-950/20">
            <summary className="cursor-pointer text-white text-sm font-semibold">
              {a("Calendar setup and TimeTree limits")}
            </summary>
            <ol className="mt-3 text-zinc-300 text-sm leading-relaxed space-y-2 list-decimal pl-5">
              <li>
                <a href="https://calendar.google.com/calendar/u/0/r/settings/addbyurl" target="_blank" rel="noreferrer" className="text-sky-300 underline hover:text-sky-200">
                  {a("Open Google Calendar: Add by URL")}
                </a>{" "}{a("on desktop, then paste your private feed URL to subscribe.")}
              </li>
              <li>{a("Refresh timing is controlled by your calendar provider; updates may be delayed.")}</li>
              <li>{a("In TimeTree, external calendars synced to your device can appear in your personal Home Calendar. Setup and calendar permissions are required.")}</li>
              <li>{a("Importing into a shared TimeTree calendar is manual. Later changes are not automatically synchronized; import them again when needed.")}</li>
            </ol>
            <div className="mt-3 flex flex-wrap gap-3 text-xs">
              <a href="https://support.timetreeapp.com/hc/en-us/articles/360000629341-How-to-Use-Other-Calendars-such-as-Google-Calendar" target="_blank" rel="noreferrer" className="text-sky-300 underline">{a("TimeTree calendar instructions")}</a>
              <a href="https://timetreeapp.com/intl/en/newsroom/2023-07-23/connectapp-api" target="_blank" rel="noreferrer" className="text-sky-300 underline">{a("TimeTree API termination notice")}</a>
            </div>
          </details>

          <div className="flex flex-wrap gap-3 items-center">
            <button
              type="button"
              onClick={rotate}
              disabled={rotating}
              data-testid="calendar-feed-rotate"
              className="text-xs uppercase tracking-[0.15em] font-bold text-amber-300 hover:text-amber-200 border border-amber-400/30 hover:border-amber-400 px-4 py-2 disabled:opacity-50"
            >
              {a(rotating ? "Rotating…" : "Regenerate URL")}
            </button>
            <div className="text-zinc-500 text-xs">
              {a("Regenerate only if the URL leaked. Existing subscriptions need the new URL.")}
            </div>
          </div>
        </>
      ) : (
        <div className="text-zinc-400 text-sm">
          {a(loading ? "Loading feed…" : "Feed not available. Refresh the page.")}
        </div>
      )}
    </div>
  );
};


const RevokeAllSessionsCard = ({ token }) => {
  const { a } = useAdminLanguage();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const revoke = async () => {
    if (!window.confirm(a("This will sign out every browser (including this one) that is currently logged in. Continue?"))) return;
    setBusy(true);
    setMsg(null);
    try {
      await axios.post(
        `${API}/admin/revoke-all-sessions`,
        {},
        { headers: { "X-Admin-Token": token } }
      );
      setMsg({ type: "ok", text: "All sessions revoked. Redirecting to login…" });
      setTimeout(() => {
        tokenStorage.clear();
        window.location.reload();
      }, 800);
    } catch (e) {
      setMsg({ type: "error", text: e?.response?.data?.detail || "Failed to revoke sessions." });
      setBusy(false);
    }
  };
  return (
    <div className="mt-8 border border-red-500/30 bg-red-500/5 p-6" data-testid="revoke-all-sessions-card">
      <div className="eyebrow !text-xs !text-red-300 mb-3 font-semibold">{a("Emergency")}</div>
      <h3 className="font-display font-bold text-white text-lg mb-2">{a("Revoke all sessions")}</h3>
      <p className="text-zinc-200 text-sm leading-relaxed mb-4">
        {a("Signs out every currently-authenticated browser — useful if you suspect a device was left signed in somewhere it shouldn't have been. Your own session is signed out too; sign back in with the current password.")}
      </p>
      {msg && (
        <div
          data-testid="revoke-all-message"
          className={`mb-4 border px-3 py-2 text-sm ${
            msg.type === "ok"
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
              : "border-red-500/40 bg-red-500/10 text-red-300"
          }`}
        >
          {a(msg.text)}
        </div>
      )}
      <button
        type="button"
        onClick={revoke}
        disabled={busy}
        data-testid="revoke-all-sessions-btn"
        className="inline-flex items-center gap-2 border border-red-500/60 text-red-200 hover:bg-red-500/20 hover:border-red-500 text-sm uppercase tracking-[0.15em] font-bold px-6 py-3 min-h-[48px] disabled:opacity-50 transition-colors"
      >
        {a(busy ? "Revoking…" : "Revoke all sessions")}
      </button>
    </div>
  );
};

const PasswordField = ({ label, value, onChange, show, setShow, testId }) => {
  const { a } = useAdminLanguage();
  return (
  <div>
    <label className="eyebrow !text-xs !text-zinc-200 mb-2.5 block font-semibold">{label}</label>
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        autoComplete="new-password"
        className="w-full bg-black border border-white/15 px-4 py-3 pr-11 text-white text-base font-mono tracking-wide focus:border-sky-400 outline-none"
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        data-testid={`${testId}-toggle`}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-sky-400 p-1.5"
        aria-label={a(show ? "Hide password" : "Show password")}
      >
        {show ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  </div>
);
};

export default Admin;
